import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Paperclip, Send, Sparkles, Users, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import AIMarkdown from "@/components/shared/AIMarkdown";

const WELCOME = {
  role: "assistant",
  content:
    "Hi — I'm NEXREQ AI. Ask me what needs attention, or paste / upload a job description and I'll surface matching candidates from your database.",
};

const HINT = {
  role: "assistant",
  content:
    "Try “What should I work on next?”, “What am I behind on?”, or “Remind me to call the client tomorrow at 10am.” You can also paste or upload a job description to find matching candidates.",
};

function CandidateCard({ candidate }) {
  return (
    <Link
      to={`/candidates/${candidate.id}`}
      data-testid={`nexreq-ai-candidate-${candidate.id}`}
      className="block rounded-lg border border-border bg-card p-3 transition-colors hover:border-[#255058]/60 hover:bg-[#255058]/5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-foreground">
            {candidate.full_name}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {candidate.current_title || "—"}
            {candidate.current_employer ? ` · ${candidate.current_employer}` : ""}
          </div>
        </div>
        {candidate.match_score != null && (
          <span className="shrink-0 rounded-full bg-[#255058] px-2 py-0.5 text-xs font-semibold text-white">
            {candidate.match_score}%
          </span>
        )}
      </div>
      {candidate.matched_skills?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {candidate.matched_skills.slice(0, 5).map((skill) => (
            <span
              key={skill}
              className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
            >
              {skill}
            </span>
          ))}
        </div>
      )}
      {candidate.reason && (
        <div className="mt-2 text-xs leading-snug text-muted-foreground">
          <AIMarkdown text={candidate.reason} size="xs" />
        </div>
      )}
    </Link>
  );
}

export default function NexreqAIPanel() {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [sessionId, setSessionId] = useState(null);
  const [greeted, setGreeted] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (!open || greeted) return;
    setGreeted(true);
    api
      .get("/nexreq-ai/briefing")
      .then(({ data }) => {
        if (data?.greeting) {
          setMessages((prev) =>
            prev.length === 1 && prev[0] === WELCOME
              ? [{ role: "assistant", content: data.greeting }, HINT]
              : prev,
          );
        }
      })
      .catch(() => {});
  }, [open, greeted]);

  const ask = async () => {
    const text = prompt.trim();
    if (!text || busy) return;
    setBusy(true);
    setPrompt("");
    setMessages((prev) => [
      ...prev,
      { role: "user", content: text },
      { role: "assistant", content: "", candidates: null, pending: true },
    ]);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nexreq-ai/chat`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: text, session_id: sessionId }),
        },
      );
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buffer = "";
      while (true) {
        const { value: chunk, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(chunk, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const event = JSON.parse(line.slice(6));
          if (event.type === "delta") {
            acc += event.content;
            setMessages((prev) => updateLast(prev, { content: acc, pending: false }));
          } else if (event.type === "candidates") {
            setMessages((prev) => updateLast(prev, { candidates: event.items, pending: false }));
          } else if (event.type === "done") {
            if (event.session_id) setSessionId(event.session_id);
          } else if (event.type === "error") {
            setMessages((prev) =>
              updateLast(prev, { content: `Sorry — ${event.message}`, pending: false }),
            );
          }
        }
      }
    } catch (err) {
      setMessages((prev) =>
        updateLast(prev, { content: "Something went wrong. Please try again.", pending: false }),
      );
    } finally {
      setBusy(false);
    }
  };

  const uploadJd = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || busy) return;
    setBusy(true);
    setMessages((prev) => [
      ...prev,
      { role: "user", content: `Uploaded JD: ${file.name}` },
      { role: "assistant", content: "Reading the job description and matching candidates…", pending: true },
    ]);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/nexreq-ai/match-candidates/upload${sessionId ? `?session_id=${sessionId}` : ""}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (data.session_id) setSessionId(data.session_id);
      setMessages((prev) =>
        updateLast(prev, {
          content: data.summary,
          candidates: data.candidates,
          pending: false,
        }),
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not process the file.";
      setMessages((prev) => updateLast(prev, { content: `Sorry — ${detail}`, pending: false }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="nexreq-ai-panel-launcher"
          aria-label="Open NEXREQ AI"
          className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#255058] text-white shadow-xl shadow-[#255058]/30 transition-transform hover:scale-105 hover:bg-[#1c3d43]"
        >
          <Sparkles className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-40 flex h-[560px] max-h-[calc(100vh-2.5rem)] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
          data-testid="nexreq-ai-panel"
        >
          <div className="flex items-center justify-between border-b border-border bg-[#255058] px-4 py-3 text-white">
            <div className="flex items-center gap-2 font-display font-semibold">
              <Bot className="h-5 w-5" />
              NEXREQ AI
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="nexreq-ai-panel-close"
              aria-label="Close"
              className="rounded-full p-1 hover:bg-white/15"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={msg.role === "user" ? "flex justify-end" : "flex justify-start"}
              >
                <div className="max-w-[90%] space-y-2">
                  {(msg.content || msg.pending) && (
                    <div
                      data-testid={`nexreq-ai-message-${msg.role}`}
                      className={
                        msg.role === "user"
                          ? "rounded-2xl rounded-br-sm bg-[#255058] px-3 py-2 text-sm text-white whitespace-pre-wrap"
                          : "rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-sm text-foreground"
                      }
                    >
                      {msg.role === "user" ? (
                        msg.content || (msg.pending ? "Thinking…" : "")
                      ) : msg.content ? (
                        <AIMarkdown text={msg.content} testId={`nexreq-ai-md-${idx}`} />
                      ) : (
                        msg.pending ? "Thinking…" : ""
                      )}
                    </div>
                  )}
                  {msg.candidates?.length > 0 && (
                    <div className="space-y-2" data-testid="nexreq-ai-candidate-list">
                      <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                        <Users className="h-3.5 w-3.5" />
                        {msg.candidates.length} matching candidate
                        {msg.candidates.length > 1 ? "s" : ""}
                      </div>
                      {msg.candidates.map((c) => (
                        <CandidateCard key={c.id} candidate={c} />
                      ))}
                    </div>
                  )}
                  {msg.candidates && msg.candidates.length === 0 && (
                    <div className="text-xs text-muted-foreground">No matching candidates found.</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-border p-3">
            <div className="flex items-end gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                className="hidden"
                onChange={uploadJd}
                data-testid="nexreq-ai-panel-file-input"
              />
              <Button
                size="icon"
                variant="outline"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                data-testid="nexreq-ai-panel-upload"
                title="Upload a job description"
                className="shrink-0"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <Textarea
                value={prompt}
                rows={1}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    ask();
                  }
                }}
                placeholder="Ask or paste a job description…"
                data-testid="nexreq-ai-panel-input"
                className="max-h-28 min-h-[40px] resize-none"
              />
              <Button
                size="icon"
                onClick={ask}
                disabled={busy}
                data-testid="nexreq-ai-panel-send"
                className="shrink-0 bg-[#255058] text-white hover:bg-[#1c3d43]"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <Link
              to="/nexreq-ai"
              onClick={() => setOpen(false)}
              className="mt-2 block text-center text-xs font-medium text-[#255058]"
              data-testid="nexreq-ai-panel-workspace-link"
            >
              Open workspace →
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

function updateLast(messages, patch) {
  const next = [...messages];
  const last = next[next.length - 1];
  next[next.length - 1] = { ...last, ...patch };
  return next;
}
